import { CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Modal, Space, Typography, message } from 'antd';
import { useState } from 'react';
import { api } from '../lib/api';

const { Text, Paragraph } = Typography;

export function SetPasswordModal({
  open,
  speakerId,
  speakerName,
  onClose,
  onDone,
  passwordEndpoint,
}: {
  open: boolean;
  speakerId: string | null;
  speakerName: string;
  onClose: () => void;
  onDone: () => void;
  /**
   * Override the API endpoint that mints the password. Defaults to
   * `/api/speakers/:id/password`. Used by the AdminUsersScene to reuse
   * this modal for team members at `/api/team-members/:id/password`.
   */
  passwordEndpoint?: string | null;
}) {
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    if (!speakerId) return;
    setBusy(true);
    setError(null);
    try {
      const url = passwordEndpoint ?? `/api/speakers/${speakerId}/password`;
      const res = await api<{ password: string }>(url, { method: 'POST' });
      setPassword(res.password);
    } catch (err) {
      setError((err as { error?: string }).error ?? 'Failed to generate password');
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setPassword(null);
    setError(null);
    onClose();
    onDone();
  };

  return (
    <Modal
      title={`Set password for ${speakerName}`}
      open={open}
      onCancel={close}
      footer={
        password ? (
          <Button type="primary" onClick={close}>
            Done
          </Button>
        ) : (
          <Space>
            <Button onClick={close}>Cancel</Button>
            <Button type="primary" loading={busy} onClick={generate}>
              Generate password
            </Button>
          </Space>
        )
      }
    >
      {password ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="Copy this password now — it won't be shown again."
            description="Read it out to the speaker over the phone or paste it into the calendar invite. They sign in with their email + this password."
          />
          <div
            style={{
              padding: '16px 20px',
              background: '#fafbfc',
              border: '1px solid #e5eaf0',
              borderRadius: 6,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 22,
              letterSpacing: 1,
              textAlign: 'center',
            }}
          >
            {password}
          </div>
          <Space>
            <Button
              icon={<CopyOutlined />}
              onClick={async () => {
                await navigator.clipboard.writeText(password);
                message.success('Copied');
              }}
            >
              Copy
            </Button>
            <Button icon={<ReloadOutlined />} onClick={generate} loading={busy}>
              Regenerate
            </Button>
          </Space>
        </Space>
      ) : (
        <Paragraph>
          Generates a one-shot random password for <Text strong>{speakerName}</Text>. They'll be
          able to sign in with their email + this password instead of waiting for a magic
          link.
        </Paragraph>
      )}
      {error && (
        <Alert type="error" message={error} style={{ marginTop: 12 }} showIcon />
      )}
    </Modal>
  );
}
